import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import { RadioListOption } from 'features/charts-v2/components/RadioListOption'
import {
  POPOVER_SHADOW,
  useFixedPopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

interface RatingTypePopoverProps {
  isOpen: boolean
  selectedValueType: number
  onSelect: (valueType: number) => void
  onClose: () => void
  cardRef?: React.RefObject<HTMLElement>
}

const OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Reviews & Ratings' },
  { value: 1, label: 'Reviews Only' },
  { value: 2, label: 'Ratings Only' },
]

// Matches FilterPopover's card-anchored pattern: portal-rendered,
// `position: fixed`, positioned by useFixedPopoverPlacement — prefers sitting
// flush above the card, flipping below when the card is too close to the top
// of the viewport (see FilterPopover for the shared rationale).
export const RatingTypePopover: React.FC<RatingTypePopoverProps> = ({
  isOpen,
  selectedValueType,
  onSelect,
  onClose,
  cardRef,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null)
  const { placement, isMeasured, style } = useFixedPopoverPlacement({
    anchorRef: cardRef,
    popoverRef,
    isOpen,
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        cardRef?.current &&
        !cardRef.current.contains(target)
      ) {
        onClose()
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, cardRef])

  // Auto-focus on open for standard focus containment while the popover is
  // open (arrow-key nav isn't needed here — Escape is handled below).
  useEffect(() => {
    if (isOpen) popoverRef.current?.focus()
  }, [isOpen])

  useEscapeDismiss({ isOpen, onClose })

  if (!isOpen) return null

  // document.body, not the ambient #popover-portal-container id — see
  // FilterSelectionPopover.tsx's popoverPortalTarget doc comment for why a
  // position: fixed overlay needs the root stacking context.
  return createPortal(
    <div
      ref={popoverRef}
      data-yogi-overlay="true"
      tabIndex={0}
      style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: POPOVER_SHADOW[placement],
        zIndex: 2000,
        padding: '8px',
        visibility: isMeasured ? 'visible' : 'hidden',
        outline: 'none',
        ...style,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {OPTIONS.map((opt) => (
        <RadioListOption
          key={opt.value}
          label={opt.label}
          isSelected={selectedValueType === opt.value}
          onClick={() => {
            onSelect(opt.value)
            onClose()
          }}
        />
      ))}
    </div>,
    document.body,
  )
}
